// MACHINERY V2 (Stroje) — "Pridať položku zo skladu" (M2.5).
//
// Spec: data package §5 "Actions": `installStockItems` PUT with
// `ChangeInstalledStockItem[]`, quantity default 1, validated 1..inStock.
// Reference impl: components/InternalStockDialog.tsx (410 lines of MUI table);
// artboard 1011 draws the v2 form — a searchable list of stock rows with a
// stepper on each selected row. This file owns the search, the picks and the
// submit; one row (and the quantity bound `maxFor`) lives in partsAddRow.tsx,
// where it moved at the M2.5 review when this file crossed the 300-line cap.
//
// THE CLAMP IS THE POINT. A quantity is only ever 1..inStockQuantity.value:
//   · The stepper cannot leave that window, and the typed input is clamped on
//     change, so an invalid payload cannot be built at all.
//   · A row with ZERO in stock cannot be selected. v1 lets you select it and
//     then shows "Max value: 0" (InternalStockDialog.tsx `getErrorState`), which
//     is a dead end dressed as a form error; the row is disabled instead.
// This is stricter than v1 and never sends anything v1 would not have sent.
//
// PAGED SOURCE, DELIBERATELY. §5: the add-dialog source is a separate PAGED
// `InternalStockItem` list — unlike the installed list, which is a bare array.
// The footer states which slice of it is on screen, as the artboard does.
//
// A PICK MAY NOT OUTLIVE THE ROW IT POINTS AT (M2.5 review finding C3, and the
// same class as C1 on the tab itself). `picked` was reset only when the dialog
// opened, so typing in the search box re-fetched page 1 with different rows
// while previously-picked ids stayed in the map — still counted by "Link (N)"
// and still sent by confirm, for rows the user could no longer see. The effect
// below reconciles `picked` against every arriving page.
//
// AND A FAILED INSTALL NO LONGER THROWS THE PICKS AWAY (M2.5 review finding R2).
// `onConfirm` used to resolve `void` whatever happened, so `onOpenChange(false)`
// ran even when the PUT had 500ed and the user lost every row and quantity they
// had picked, with only a toast to show for it. It now resolves a BOOLEAN
// (usePartsTabActions.ts — one PUT for the whole batch, so there is no per-row
// outcome to report), the dialog closes only on true, and on false it stays open
// with the selection intact behind a RetryNotice that re-runs the same install.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useEffect, useMemo, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import {
  changeInternalStockItemsQuery,
  getInternalStockItems,
} from "../../../customerInternalStockSlice";
import {
  ChangeInstalledStockItem,
  InternalStockItem,
} from "../../../../inventory/models/models";
import RetryNotice from "../../feedback/RetryNotice";
import TabDialog from "./TabDialog";
import { TabSearch } from "./tabAtoms";
import { StockPickRow, maxFor } from "./partsAddRow";

export interface PartsAddDialogV2Props {
  machineId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Resolves TRUE only when every picked item was linked. Anything else keeps
   *  this dialog open with the picks intact (R2). */
  onConfirm: (items: ChangeInstalledStockItem[]) => Promise<boolean>;
}

const PartsAddDialogV2: React.FC<PartsAddDialogV2Props> = ({
  machineId,
  open,
  onOpenChange,
  onConfirm,
}) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("myMachinery");
  const page = useAppSelector((s) => s.customerInternalStock.internalStockItems);
  const query = useAppSelector((s) => s.customerInternalStock.internalStockItemsQuery);

  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  /** The last install attempt was rejected (R2). */
  const [failed, setFailed] = useState(false);
  /** False until the user actually types, so opening the dialog fires ONE fetch
   *  (the open effect) instead of two. */
  const searchTouched = useRef(false);

  useEffect(() => {
    if (!open) return;
    setPicked({});
    setSearch("");
    setFailed(false);
    searchTouched.current = false;
    const next = { ...query, machineId, search: undefined, pageNo: 1 };
    dispatch(changeInternalStockItemsQuery(next));
    dispatch(getInternalStockItems(next));
    // Opening is the trigger; re-running on every query change would fight the
    // debounce below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, machineId, dispatch]);

  useEffect(() => {
    if (!open || !searchTouched.current) return undefined;
    const timer = setTimeout(() => {
      const next = { ...query, machineId, search: search || undefined, pageNo: 1 };
      dispatch(changeInternalStockItemsQuery(next));
      dispatch(getInternalStockItems(next));
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const items = useMemo(() => page?.items || [], [page]);

  // C3: every arriving page reconciles the picks — an id that is no longer on
  // screen must not keep counting toward "Link (N)" or ride along in confirm.
  useEffect(() => {
    setPicked((prev) => {
      const ids = Object.keys(prev);
      if (ids.length === 0) return prev;
      const next: Record<string, number> = {};
      ids.forEach((id) => {
        if (items.some((item) => item.id === id)) next[id] = prev[id];
      });
      return Object.keys(next).length === ids.length ? prev : next;
    });
  }, [items]);

  const selectedCount = Object.keys(picked).length;

  const toggle = (item: InternalStockItem) => {
    const max = maxFor(item);
    if (max < 1) return;
    setPicked((prev) => {
      const next = { ...prev };
      if (next[item.id] !== undefined) delete next[item.id];
      else next[item.id] = 1;
      return next;
    });
  };

  const setQty = (item: InternalStockItem, value: number) => {
    const max = maxFor(item);
    const clamped = Math.min(Math.max(Math.round(value) || 1, 1), Math.max(max, 1));
    setPicked((prev) => ({ ...prev, [item.id]: clamped }));
  };

  const confirm = async () => {
    if (!selectedCount || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const linked = await onConfirm(
        Object.entries(picked).map(([stockItemId, quantity]) => ({ stockItemId, quantity }))
      );
      // R2: closing is earned, not automatic. A rejected PUT leaves the picks
      // and their quantities exactly where the user left them.
      if (linked) onOpenChange(false);
      else setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const from = page ? (page.pageNo - 1) * page.pageSize + 1 : 0;
  const to = page ? Math.min(page.pageNo * page.pageSize, page.totalCount) : 0;

  return (
    <TabDialog
      open={open}
      onOpenChange={onOpenChange}
      title={translate("myMachinery:v2.tabs.parts.addTitle")}
      glyph={<span style={{ fontSize: 13 }}>&#9881;</span>}
      width={620}
      footerNote={translate("myMachinery:v2.tabs.parts.qtyNote")}
      footer={
        <>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {translate("common:buttons.cancel")}
          </button>
          <button
            type="button"
            className="btn btn-mod"
            onClick={confirm}
            disabled={busy || selectedCount === 0}
          >
            {translate("myMachinery:v2.tabs.parts.linkCta", { count: selectedCount })}
          </button>
        </>
      }
    >
      <TabSearch
        value={search}
        onChange={(v) => {
          searchTouched.current = true;
          setSearch(v);
        }}
        placeholder={translate("myMachinery:v2.tabs.parts.addSearch")}
        clearLabel={translate("myMachinery:v2.tabs.clearSearch")}
        maxWidth={1200}
      />

      {failed && (
        <div style={{ marginTop: 12 }}>
          <RetryNotice
            title={translate("myMachinery:v2.tabs.parts.installFailed")}
            hint={translate("myMachinery:v2.tabs.parts.installFailedHint")}
            onRetry={confirm}
            busy={busy}
          />
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12 }}>
        {items.map((item) => (
          <StockPickRow
            key={item.id}
            item={item}
            quantity={picked[item.id]}
            onToggle={() => toggle(item)}
            onQuantity={(v) => setQty(item, v)}
          />
        ))}
      </div>

      {page && page.totalCount > 0 && (
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9.5,
            color: "var(--faint)",
            letterSpacing: "0.04em",
            marginTop: 10,
          }}
        >
          {translate("myMachinery:v2.tabs.parts.pageInfo", {
            from,
            to,
            total: page.totalCount,
          })}
        </div>
      )}
    </TabDialog>
  );
};

export default PartsAddDialogV2;
